// FingerprintJS service for visitor identification
// This service handles initialization, generation, storage, and retrieval of visitor IDs

/**
 * Interface for FingerprintJS result
 */
interface FingerprintResult {
  visitorId: string;
  [key: string]: any;
}

/**
 * Class to handle FingerprintJS operations
 */
export class FingerprintService {
  private static instance: FingerprintService;
  private fpPromise: Promise<any> | null = null;
  private visitorId: string | null = null;
  private readonly storageKey = 'forza_visitor_id';
  private readonly maxRetries = 3;
  private retryCount = 0;
  private readonly retryDelay = 1000; // 1 second

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance of FingerprintService
   */
  public static getInstance(): FingerprintService {
    if (!FingerprintService.instance) {
      FingerprintService.instance = new FingerprintService();
    }
    return FingerprintService.instance;
  }

  /**
   * Initialize FingerprintJS
   */
  public async init(): Promise<void> {
    try {
      // Try to get stored visitor ID first
      const storedVisitorId = this.getStoredVisitorId();
      if (storedVisitorId) {
        this.visitorId = storedVisitorId;
        return;
      }

      // Initialize FingerprintJS
      this.fpPromise = import('https://fpjscdn.net/v3/MCl1ywafi4TCVIp4WxnM')
        .then(FingerprintJS => FingerprintJS.load({
          region: "eu"
        }));

      // Generate visitor ID
      await this.generateVisitorId();
    } catch (error) {
      console.error('Failed to initialize FingerprintJS:', error);
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => this.init(), this.retryDelay * this.retryCount);
      } else {
        // Use a fallback ID if FingerprintJS fails
        this.useFallbackId();
      }
    }
  }

  /**
   * Generate a visitor ID using FingerprintJS
   */
  private async generateVisitorId(): Promise<void> {
    try {
      if (!this.fpPromise) {
        throw new Error('FingerprintJS not initialized');
      }

      const fp = await this.fpPromise;
      const result: FingerprintResult = await fp.get();
      this.visitorId = result.visitorId;
      this.storeVisitorId(this.visitorId);
    } catch (error) {
      console.error('Failed to generate visitor ID:', error);
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => this.generateVisitorId(), this.retryDelay * this.retryCount);
      } else {
        // Use a fallback ID if visitor ID generation fails
        this.useFallbackId();
      }
    }
  }

  /**
   * Get the visitor ID
   */
  public getVisitorId(): string {
    // If we don't have a visitor ID yet, return a fallback
    if (!this.visitorId) {
      return this.useFallbackId();
    }
    return this.visitorId;
  }

  /**
   * Store the visitor ID in localStorage
   */
  private storeVisitorId(visitorId: string): void {
    try {
      localStorage.setItem(this.storageKey, visitorId);
    } catch (error) {
      console.error('Failed to store visitor ID:', error);
      // Try to use cookies as fallback
      this.storeVisitorIdInCookie(visitorId);
    }
  }

  /**
   * Store the visitor ID in a cookie (fallback)
   */
  private storeVisitorIdInCookie(visitorId: string): void {
    try {
      document.cookie = `${this.storageKey}=${visitorId}; max-age=${60 * 60 * 24 * 365}; path=/`;
    } catch (error) {
      console.error('Failed to store visitor ID in cookie:', error);
    }
  }

  /**
   * Get the stored visitor ID from localStorage or cookie
   */
  private getStoredVisitorId(): string | null {
    try {
      // Try localStorage first
      const visitorId = localStorage.getItem(this.storageKey);
      if (visitorId) {
        return visitorId;
      }

      // Try cookies as fallback
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === this.storageKey) {
          return value;
        }
      }
    } catch (error) {
      console.error('Failed to retrieve stored visitor ID:', error);
    }
    return null;
  }

  /**
   * Generate a fallback ID when FingerprintJS fails
   */
  private useFallbackId(): string {
    // Check if we already have a fallback ID stored
    const storedFallbackId = this.getStoredVisitorId();
    if (storedFallbackId) {
      this.visitorId = storedFallbackId;
      return storedFallbackId;
    }

    // Generate a random ID as fallback
    const fallbackId = 'fallback_' + Math.random().toString(36).substring(2, 15) + 
                       Math.random().toString(36).substring(2, 15);
    this.visitorId = fallbackId;
    this.storeVisitorId(fallbackId);
    return fallbackId;
  }
}

// Export a singleton instance
export const fingerprintService = FingerprintService.getInstance();